import { useEffect, useRef, useMemo } from 'react';
import Sidebar from '../../../components/Sidebar';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { MedicalStoreValidationSchema } from '../../../utils/FormValidation';
import Header from '../../../components/Header';
import html2canvas from 'html2canvas';
import {
  ActionGetMedicalDetailByID,
  ActionUpdateMedicalDetail,
} from '../../../store/Actions/MedicalStoreAction';
import { Divider, DatePicker, Table, Card, Tag, Typography, Space, Button, message, Collapse } from 'antd';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// --- Sub-components for better DRY ---

const InfoItem = ({ label, value, color = '#64748b' }) => (
  <div style={{ marginBottom: '12px' }}>
    <Text style={{ fontSize: '12px', color, textTransform: 'uppercase', fontWeight: 600, display: 'block', letterSpacing: '0.5px' }}>
      {label}
    </Text>
    <Text style={{ fontSize: '15px', color: '#1e293b', fontWeight: 500 }}>
      {value || '---'}
    </Text>
  </div>
);

const SectionHeader = ({ title, icon }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>
    {icon && <FeatherIcon icon={icon} size={18} style={{ color: '#0f172a' }} />}
    <Title level={5} style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>{title}</Title>
  </div>
);

const EditMedicalStoreDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const captureRef = useRef(null);

  const pageValue = searchParams.get('page') || 1;
  const idValue = searchParams.get('id');
  const { medicalStore } = useSelector(state => state?.MedicalStore);

  useEffect(() => {
    if (idValue) {
      dispatch(ActionGetMedicalDetailByID({ Query: `/${idValue}` }));
    }
  }, [idValue, dispatch]);

  const handleDownloadImage = async () => {
    try {
      const captureEl = captureRef.current;
      if (!captureEl) return;

      const canvas = await html2canvas(captureEl, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        scale: 2,
      });

      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `Prescription-${medicalStore?.customerId?.name || 'patient'}.png`;
      link.click();
    } catch (err) {
      message.error("Failed to download image");
      console.error(err);
    }
  };

  const initialValues = useMemo(() => {
    if (!medicalStore) return null;
    return {
      dateOfMedicalStore: medicalStore?.nextConsultationDate ? dayjs(medicalStore.nextConsultationDate) : dayjs(),
      recomendedMedicines: medicalStore?.recomendedMedicines || [],
    };
  }, [medicalStore]);

  if (!medicalStore || !medicalStore.customerId) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const patient = medicalStore.customerId;

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb & Top Actions */}
          <div className="page-header" style={{ marginBottom: '24px' }}>
            <div className="row align-items-center">
              <div className="col">
                <ul className="breadcrumb" style={{ background: 'transparent', padding: 0, flexWrap: 'nowrap', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                  <li className="breadcrumb-item"><Link to="/medical-store">Medical Store</Link></li>
                  <li className="breadcrumb-item active" style={{ display: 'flex', alignItems: 'center' }}>
                    <FeatherIcon icon="chevron-right" size={14} style={{ margin: '0 8px', flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>Dispensing Details</span>
                  </li>
                </ul>
                <Title level={3} className="page-title" style={{ margin: '8px 0 0' }}>Patient Prescription</Title>
              </div>
              <div className="col-auto mt-3 mt-md-0">
                <Space wrap>
                  <Button
                    icon={<FeatherIcon icon="download" size={16} />}
                    onClick={handleDownloadImage}
                    block
                  >
                    Download
                  </Button>
                </Space>
              </div>
            </div>
          </div>

          <Formik
            enableReinitialize
            initialValues={initialValues || {}}
            validationSchema={MedicalStoreValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
              dispatch(ActionUpdateMedicalDetail({
                Query: `/${idValue}`,
                obj: {
                  isPurchased: true,
                  dateOfMedicalStore: values.dateOfMedicalStore?.format('YYYY-MM-DD'),
                  purchasedMedicines: values.recomendedMedicines,
                },
              }, (res) => {
                setSubmitting(false);
                if (res?.statusCode === 200) {
                  message.success("Prescription updated successfully");
                  navigate(`/medical-store?page=${pageValue}`);
                }
              }));
            }}
          >
            {({ values, errors, touched, handleSubmit, setFieldValue, isSubmitting }) => (
              <div className="row g-4" ref={captureRef} style={{ background: '#f8fafc', padding: '10px' }}>
                {/* Left Column: Patient & Clinical Info */}
                <div className="col-lg-4">
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {/* Patient Card - Always Visible */}
                    <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                      <SectionHeader title="Patient Profile" icon="user" />
                      <div className="patient-info-grid">
                        <InfoItem label="Full Name" value={patient.name} />
                        <InfoItem label="Reg No" value={patient.registrationNo} />
                        <InfoItem label="Phone" value={patient.phoneNo} />
                        <InfoItem label="Gender" value={patient.gender} />
                        <InfoItem label="Age" value={patient.birthDate ? dayjs().diff(patient.birthDate, 'year') : '---'} />
                        <InfoItem label="Old Case No" value={patient.oldCasePaperNo} />
                      </div>
                      <Divider style={{ margin: '12px 0' }} />
                      <InfoItem label="Address" value={patient.address} />
                    </Card>

                    <Collapse
                      defaultActiveKey={['1']}
                      ghost
                      expandIconPosition="end"
                      style={{ background: 'transparent' }}
                    >
                      {/* Clinical Context - Collapsible */}
                      <Collapse.Panel
                        header={<SectionHeader title="Clinical Notes" icon="activity" />}
                        key="1"
                        style={{ background: '#ffffff', borderRadius: '12px', marginBottom: '16px', border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}
                      >
                        <InfoItem label="Diagnosis" value={medicalStore.diagnosis?.[0]?.diagnosis} />

                        {medicalStore.virechan?.length > 0 && medicalStore.virechan[0] !== "false" && (
                          <div style={{ marginTop: '12px', padding: '12px', background: '#fff8f1', borderRadius: '8px', border: '1px solid #ffedd5' }}>
                            <Text strong style={{ color: '#9a3412', fontSize: '12px', textTransform: 'uppercase' }}>Virechan Instructions</Text>
                            <div style={{ color: '#c2410c', marginTop: '4px', fontWeight: 600 }}>{medicalStore.virechan.join(', ')}</div>
                          </div>
                        )}

                        {medicalStore.doctorNotes?.length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                            <Text type="secondary" style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>Special Instructions</Text>
                            <div style={{ marginTop: '4px', fontStyle: 'italic', color: '#475569' }}>
                              {medicalStore.doctorNotes[0]}
                            </div>
                          </div>
                        )}
                      </Collapse.Panel>

                      {/* Dietary Advice - Collapsible */}
                      {(medicalStore.dos?.length > 0 || medicalStore.donts?.length > 0) && (
                        <Collapse.Panel
                          header={<SectionHeader title="Dietary Advice" icon="coffee" />}
                          key="2"
                          style={{ background: '#ffffff', borderRadius: '12px', border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}
                        >
                          {medicalStore.dos?.length > 0 && (
                            <div style={{ marginBottom: '12px' }}>
                              <Tag color="success">DO&apos;S</Tag>
                              <ul style={{ paddingLeft: '20px', marginTop: '8px', fontSize: '13px' }}>
                                {medicalStore.dos.map((item, idx) => <li key={idx}>{item.title}</li>)}
                              </ul>
                            </div>
                          )}
                          {medicalStore.donts?.length > 0 && (
                            <div>
                              <Tag color="error">DON&apos;TS</Tag>
                              <ul style={{ paddingLeft: '20px', marginTop: '8px', fontSize: '13px' }}>
                                {medicalStore.donts.map((item, idx) => <li key={idx}>{item.title}</li>)}
                              </ul>
                            </div>
                          )}
                        </Collapse.Panel>
                      )}
                    </Collapse>
                  </Space>
                </div>

                {/* Right Column: Prescription Table & Submission */}
                <div className="col-lg-8">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Medicine Table */}
                    <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                      <SectionHeader title="Recommended Medicines" icon="plus-circle" />
                      <Table
                        dataSource={values.recomendedMedicines}
                        pagination={false}
                        bordered={false}
                        rowKey={(record, idx) => idx}
                        className="custom-med-table"
                        scroll={{ x: 600 }}
                        columns={[
                          {
                            title: 'Medicine Name',
                            dataIndex: 'medicineName',
                            key: 'medicineName',
                            render: (text) => <Text strong style={{ color: '#0f172a' }}>{text}</Text>
                          },
                          {
                            title: 'Timing',
                            dataIndex: 'whenToTake',
                            key: 'whenToTake',
                            render: (val) => Array.isArray(val) ? val.join(', ') : val
                          },
                          {
                            title: 'Dosage',
                            dataIndex: 'howMuch',
                            key: 'howMuch',
                            align: 'center',
                          },
                          {
                            title: 'Course',
                            dataIndex: 'course',
                            key: 'course',
                            render: (text) => <Tag style={{ borderRadius: '4px' }}>{text}</Tag>
                          },
                          {
                            title: 'Days',
                            dataIndex: 'days',
                            key: 'days',
                            width: 100,
                            render: (val, record, index) => (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                style={{ width: '70px', textAlign: 'center' }}
                                value={val}
                                onChange={(e) => {
                                  const newMeds = [...values.recomendedMedicines];
                                  newMeds[index] = { ...newMeds[index], days: e.target.value };
                                  setFieldValue('recomendedMedicines', newMeds);
                                }}
                                placeholder="Qty"
                              />
                            )
                          },
                          {
                            title: 'Anupan',
                            dataIndex: 'anupan',
                            key: 'anupan',
                            render: (text) => <Text type="secondary" style={{ fontSize: '13px' }}>{text}</Text>
                          }
                        ]}
                      />
                    </Card>

                    {/* Footer Actions */}
                    <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                      <div className="row g-3 align-items-center">
                        <div className="col-12 col-md-6">
                          <label style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px', display: 'block' }}>
                            NEXT CONSULTATION DATE
                          </label>
                          <DatePicker
                            style={{ width: '100%', height: '42px', borderRadius: '8px' }}
                            value={values.dateOfMedicalStore}
                            onChange={(val) => setFieldValue('dateOfMedicalStore', val)}
                            format="DD-MM-YYYY"
                          />
                        </div>
                        <div className="col-12 col-md-6 text-md-end">
                          <Button
                            type="primary"
                            size="large"
                            className="w-100 w-md-auto"
                            style={{ height: '42px', padding: '0 40px', borderRadius: '8px', fontWeight: 600, background: '#0f172a', borderColor: '#0f172a' }}
                            onClick={() => handleSubmit()}
                            loading={isSubmitting}
                          >
                            Mark as Purchased
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            )}
          </Formik>
        </div>
      </div>
      <style>{`
        .custom-med-table .ant-table-thead > tr > th {
          background: #f8fafc;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.5px;
          font-weight: 700;
          color: #64748b;
          border-bottom: 2px solid #f1f5f9;
        }
        .custom-med-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 16px 8px;
        }
        .custom-med-table .ant-table-tbody > tr:hover > td {
          background: #f1f5f9 !important;
        }
        .patient-info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        @media (max-width: 576px) {
          .patient-info-grid {
            grid-template-columns: 1fr;
          }
          .page-title {
            font-size: 1.5rem !important;
          }
          .content {
            padding: 15px !important;
          }
          .w-md-auto {
            width: auto !important;
          }
          .ant-card-body {
            padding: 16px !important;
          }
          .custom-med-table .ant-table-tbody > tr > td {
            padding: 12px 8px !important;
          }
        }
        @media print {
          .page-wrapper { margin: 0 !important; padding: 0 !important; }
          .sidebar, .header, .breadcrumb, .btn, .ant-btn { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default EditMedicalStoreDetails;
